import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAdminSession } from "@/hooks/use-admin-session";
import { listCockpitTexts, setCockpitText } from "@/lib/cockpit-texts.functions";
import {
  COCKPIT_TEXT_MAX_LENGTH,
  type CockpitTextEntry,
} from "@/lib/cockpit-texts";

/**
 * Ticket KN4: Pflege kundenseitiger Cockpit-Texte.
 *
 * Die Seite listet alle registrierten Texte auf (`COCKPIT_TEXT_KEYS`). Ein
 * weiterer Text ist deshalb eine Zeile in der Registrierung – hier ist kein
 * Umbau nötig. Ändern dürfen ausschließlich Administratoren, jede Änderung
 * wird protokolliert.
 */
export const Route = createFileRoute("/_admin/admin/cockpit-texte")({
  head: () => ({
    meta: [
      { title: "Cockpit-Texte – Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CockpitTextsPage,
});

function CockpitTextsPage() {
  const session = useAdminSession();
  const load = useServerFn(listCockpitTexts);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "cockpit-texts"],
    queryFn: () => load(),
  });

  return (
    <div className="mx-auto max-w-3xl">
      <header className="mb-8">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-primary">
          Cockpit-Texte
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Texte, die Kundinnen und Kunden im Cockpit lesen. Änderungen wirken
          sofort und werden protokolliert. Bearbeiten dürfen ausschließlich
          Administratoren. Die Fragen und Antworten der Cockpit-Hilfe (FAQ)
          werden derzeit über die Agentur gepflegt.
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Texte werden geladen …</p>
      ) : !data || data.length === 0 ? (
        <p className="text-sm text-muted-foreground">Es sind keine Texte hinterlegt.</p>
      ) : (
        <div className="space-y-6">
          {data.map((entry) => (
            <TextEditor key={entry.key} entry={entry} canEdit={session.isAdmin} />
          ))}
        </div>
      )}
    </div>
  );
}

function TextEditor({ entry, canEdit }: { entry: CockpitTextEntry; canEdit: boolean }) {
  const [value, setValue] = useState(entry.value);
  const queryClient = useQueryClient();
  const store = useServerFn(setCockpitText);

  useEffect(() => {
    setValue(entry.value);
  }, [entry.value]);

  const mutation = useMutation({
    mutationFn: () => store({ data: { key: entry.key, value: value.trim() } }),
    onSuccess: () => {
      toast.success("Text gespeichert.");
      queryClient.invalidateQueries({ queryKey: ["admin", "cockpit-texts"] });
    },
    onError: () => toast.error("Text konnte nicht gespeichert werden."),
  });

  const dirty = value.trim() !== entry.value.trim();
  const tooLong = value.trim().length > COCKPIT_TEXT_MAX_LENGTH;

  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <h2 className="font-display text-base font-extrabold text-foreground">{entry.label}</h2>
      {entry.description ? (
        <p className="mt-1 text-sm text-muted-foreground">{entry.description}</p>
      ) : null}

      <Textarea
        className="mt-4 min-h-[160px]"
        value={value}
        disabled={!canEdit || mutation.isPending}
        onChange={(e) => setValue(e.target.value)}
        aria-label={entry.label}
      />

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[11px] text-muted-foreground/70">{entry.key}</p>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {value.trim().length} / {COCKPIT_TEXT_MAX_LENGTH} Zeichen
          </span>
          <Button
            type="button"
            disabled={!canEdit || !dirty || tooLong || value.trim().length === 0 || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Speichern
          </Button>
        </div>
      </div>

      {!canEdit ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Als Editorin oder Editor können Sie den Text nur ansehen. Änderungen sind
          Administratoren vorbehalten.
        </p>
      ) : null}
    </section>
  );
}
